<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import InboxesAPI from 'dashboard/api/inboxes';
import ButtonV4 from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();

// https://developers.facebook.com/docs/whatsapp/cloud-api/reference/business-profiles
const VERTICALS = [
  'UNDEFINED',
  'OTHER',
  'AUTO',
  'BEAUTY',
  'APPAREL',
  'EDU',
  'ENTERTAIN',
  'EVENT_PLAN',
  'FINANCE',
  'GROCERY',
  'GOVT',
  'HOTEL',
  'HEALTH',
  'NONPROFIT',
  'PROF_SERVICES',
  'RETAIL',
  'TRAVEL',
  'RESTAURANT',
  'NOT_A_BIZ',
];

const LIMITS = {
  about: 139,
  address: 256,
  description: 512,
  email: 128,
  website: 256,
};

const profile = reactive({
  about: '',
  address: '',
  description: '',
  email: '',
  vertical: 'UNDEFINED',
  websites: ['', ''],
});

const profilePictureUrl = ref('');
const verifiedName = ref('');
const profilePictureHandle = ref('');
const isLoading = ref(false);
const isSaving = ref(false);
const isUploading = ref(false);
const fileInputRef = ref(null);

const verticalOptions = computed(() =>
  VERTICALS.map(value => ({
    value,
    label: t(`INBOX_MGMT.WHATSAPP_PROFILE.VERTICALS.${value}`),
  }))
);

const emailError = computed(() => {
  if (!profile.email) return '';
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(profile.email)
    ? ''
    : t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.EMAIL.ERROR');
});

// Meta's verified name is what customers actually see; the inbox name is internal.
const previewName = computed(() => verifiedName.value || props.inbox.name);
const previewPhoneNumber = computed(() => props.inbox.phone_number);
const previewWebsites = computed(() => profile.websites.filter(Boolean));
const previewCategory = computed(() =>
  profile.vertical && profile.vertical !== 'UNDEFINED'
    ? t(`INBOX_MGMT.WHATSAPP_PROFILE.VERTICALS.${profile.vertical}`)
    : ''
);
const previewInitial = computed(() =>
  (previewName.value || '?').charAt(0).toUpperCase()
);

const fetchProfile = async () => {
  isLoading.value = true;
  try {
    const { data } = await InboxHealthAPI.getWhatsappProfile(props.inbox.id);
    profile.about = data.about || '';
    profile.address = data.address || '';
    profile.description = data.description || '';
    profile.email = data.email || '';
    profile.vertical = data.vertical || 'UNDEFINED';
    profile.websites = [data.websites?.[0] || '', data.websites?.[1] || ''];
    profilePictureUrl.value = data.profile_picture_url || '';
    verifiedName.value = data.verified_name || '';
    // Meta was unreachable, so the backend served the last synced copy
    if (data.stale) {
      useAlert(t('INBOX_MGMT.WHATSAPP_PROFILE.STALE', { error: data.error }));
      return false;
    }
    return true;
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.WHATSAPP_PROFILE.FETCH_ERROR')
    );
    return false;
  } finally {
    isLoading.value = false;
  }
};

// Pulls whatever is live on Meta right now — covers existing inboxes onboarded
// before this screen existed, and edits made from the WhatsApp Business app.
const importProfile = async () => {
  profilePictureHandle.value = '';
  const imported = await fetchProfile();
  if (imported) useAlert(t('INBOX_MGMT.WHATSAPP_PROFILE.IMPORT_SUCCESS'));
};

const onPickFile = () => fileInputRef.value?.click();

const onFileChange = async event => {
  const file = event.target.files?.[0];
  if (!file) return;

  isUploading.value = true;
  try {
    const { data } = await InboxesAPI.metaUpload(props.inbox.id, file);
    profilePictureHandle.value = data.h;
    profilePictureUrl.value = URL.createObjectURL(file);
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.WHATSAPP_PROFILE.PICTURE.UPLOAD_ERROR')
    );
  } finally {
    isUploading.value = false;
    event.target.value = '';
  }
};

const updateProfile = async () => {
  if (emailError.value) return;

  isSaving.value = true;
  try {
    const payload = {
      about: profile.about,
      address: profile.address,
      description: profile.description,
      email: profile.email,
      vertical: profile.vertical,
      websites: previewWebsites.value,
    };
    if (profilePictureHandle.value) {
      payload.profile_picture_handle = profilePictureHandle.value;
    }
    const { data } = await InboxHealthAPI.updateWhatsappProfile(
      props.inbox.id,
      payload
    );
    profilePictureHandle.value = '';
    profilePictureUrl.value =
      data.profile_picture_url || profilePictureUrl.value;
    useAlert(t('INBOX_MGMT.WHATSAPP_PROFILE.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.WHATSAPP_PROFILE.UPDATE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

onMounted(fetchProfile);
</script>

<template>
  <div class="grid grid-cols-1 gap-8 lg:grid-cols-2">
    <div class="flex flex-col gap-5">
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.DESCRIPTION') }}
        </p>
      </div>

      <div class="flex items-center gap-4">
        <div
          class="flex items-center justify-center overflow-hidden rounded-full size-16 bg-n-alpha-2"
        >
          <img
            v-if="profilePictureUrl"
            :src="profilePictureUrl"
            class="object-cover size-16"
            alt=""
          />
          <Icon v-else icon="i-lucide-image" class="size-5 text-n-slate-11" />
        </div>
        <div class="flex flex-col gap-1">
          <ButtonV4
            sm
            faded
            slate
            :is-loading="isUploading"
            :label="$t('INBOX_MGMT.WHATSAPP_PROFILE.PICTURE.UPLOAD')"
            @click="onPickFile"
          />
          <span class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.PICTURE.HINT') }}
          </span>
        </div>
        <input
          ref="fileInputRef"
          type="file"
          accept="image/jpeg,image/png"
          class="hidden"
          @change="onFileChange"
        />
      </div>

      <TextArea
        v-model="profile.about"
        show-character-count
        :max-length="LIMITS.about"
        :label="$t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.ABOUT.LABEL')"
        :placeholder="
          $t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.ABOUT.PLACEHOLDER')
        "
        :message="$t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.ABOUT.HELP')"
      />

      <TextArea
        v-model="profile.description"
        show-character-count
        :max-length="LIMITS.description"
        :label="$t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.DESCRIPTION.LABEL')"
        :placeholder="
          $t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.DESCRIPTION.PLACEHOLDER')
        "
      />

      <TextArea
        v-model="profile.address"
        show-character-count
        :max-length="LIMITS.address"
        :label="$t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.ADDRESS.LABEL')"
        :placeholder="
          $t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.ADDRESS.PLACEHOLDER')
        "
      />

      <Input
        v-model="profile.email"
        type="email"
        :maxlength="LIMITS.email"
        :label="$t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.EMAIL.LABEL')"
        :placeholder="
          $t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.EMAIL.PLACEHOLDER')
        "
        :message="emailError"
        :message-type="emailError ? 'error' : 'info'"
      />

      <Input
        v-model="profile.websites[0]"
        :maxlength="LIMITS.website"
        :label="$t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.WEBSITE_ONE.LABEL')"
        placeholder="https://example.com"
      />

      <Input
        v-model="profile.websites[1]"
        :maxlength="LIMITS.website"
        :label="$t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.WEBSITE_TWO.LABEL')"
        placeholder="https://shop.example.com"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.VERTICAL.LABEL') }}
        </label>
        <Select v-model="profile.vertical" :options="verticalOptions" />
      </div>

      <div class="flex items-center gap-2">
        <ButtonV4
          :is-loading="isSaving"
          :disabled="isLoading || isSaving || !!emailError"
          :label="$t('INBOX_MGMT.WHATSAPP_PROFILE.SAVE')"
          @click="updateProfile"
        />
        <ButtonV4
          faded
          slate
          icon="i-lucide-refresh-cw"
          :is-loading="isLoading"
          :disabled="isLoading || isSaving"
          :label="$t('INBOX_MGMT.WHATSAPP_PROFILE.IMPORT')"
          @click="importProfile"
        />
      </div>
      <p class="-mt-3 text-xs text-n-slate-11">
        {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.IMPORT_HELP') }}
      </p>
    </div>

    <div class="flex flex-col gap-3">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.PREVIEW.TITLE') }}
      </h3>
      <div
        class="w-full max-w-sm overflow-hidden shadow-lg rounded-3xl bg-[#ECE5DD] dark:bg-[#0B141A]"
      >
        <div class="flex items-center gap-3 px-4 py-3 bg-[#075E54]">
          <Icon icon="i-lucide-arrow-left" class="size-4 text-white" />
          <span class="text-sm font-medium text-white">
            {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.PREVIEW.HEADER') }}
          </span>
        </div>

        <div
          class="flex flex-col items-center gap-1 px-4 py-6 bg-white dark:bg-[#111B21]"
        >
          <div
            class="flex items-center justify-center overflow-hidden rounded-full size-24 bg-[#DFE5E7] dark:bg-[#202C33]"
          >
            <img
              v-if="profilePictureUrl"
              :src="profilePictureUrl"
              class="object-cover size-24"
              alt=""
            />
            <span
              v-else
              class="text-3xl font-medium text-[#8696A0] dark:text-[#8696A0]"
            >
              {{ previewInitial }}
            </span>
          </div>
          <span
            class="mt-2 text-lg font-medium text-[#111B21] dark:text-[#E9EDEF]"
          >
            {{ previewName }}
          </span>
          <span class="text-sm text-[#667781] dark:text-[#8696A0]">
            {{ previewPhoneNumber }}
          </span>
          <span
            class="px-2 py-0.5 mt-1 text-xs rounded-full bg-[#D9FDD3] text-[#046307] dark:bg-[#0B3D2E] dark:text-[#25D366]"
          >
            {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.PREVIEW.BUSINESS_ACCOUNT') }}
          </span>
        </div>

        <div class="mt-2 px-4 py-3 bg-white dark:bg-[#111B21]">
          <p class="text-xs text-[#667781] dark:text-[#8696A0]">
            {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.FIELDS.ABOUT.LABEL') }}
          </p>
          <p
            class="text-sm whitespace-pre-line text-[#111B21] dark:text-[#E9EDEF]"
          >
            {{
              profile.about || $t('INBOX_MGMT.WHATSAPP_PROFILE.PREVIEW.EMPTY')
            }}
          </p>
        </div>

        <div
          class="flex flex-col gap-4 px-4 py-3 mt-2 bg-white dark:bg-[#111B21]"
        >
          <p class="text-sm font-medium text-[#008069] dark:text-[#25D366]">
            {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.PREVIEW.BUSINESS_INFO') }}
          </p>

          <div v-if="profile.description" class="flex items-start gap-3">
            <Icon
              icon="i-lucide-info"
              class="mt-0.5 size-4 text-[#667781] dark:text-[#8696A0]"
            />
            <p
              class="text-sm whitespace-pre-line text-[#111B21] dark:text-[#E9EDEF]"
            >
              {{ profile.description }}
            </p>
          </div>

          <div v-if="profile.address" class="flex items-start gap-3">
            <Icon
              icon="i-lucide-map-pin"
              class="mt-0.5 size-4 text-[#667781] dark:text-[#8696A0]"
            />
            <p
              class="text-sm whitespace-pre-line text-[#111B21] dark:text-[#E9EDEF]"
            >
              {{ profile.address }}
            </p>
          </div>

          <div v-if="profile.email" class="flex items-start gap-3">
            <Icon
              icon="i-lucide-mail"
              class="mt-0.5 size-4 text-[#667781] dark:text-[#8696A0]"
            />
            <p class="text-sm break-all text-[#111B21] dark:text-[#E9EDEF]">
              {{ profile.email }}
            </p>
          </div>

          <div
            v-for="website in previewWebsites"
            :key="website"
            class="flex items-start gap-3"
          >
            <Icon
              icon="i-lucide-link"
              class="mt-0.5 size-4 text-[#667781] dark:text-[#8696A0]"
            />
            <p class="text-sm break-all text-[#027EB5] dark:text-[#53BDEB]">
              {{ website }}
            </p>
          </div>

          <div v-if="previewCategory" class="flex items-start gap-3">
            <Icon
              icon="i-lucide-briefcase"
              class="mt-0.5 size-4 text-[#667781] dark:text-[#8696A0]"
            />
            <p class="text-sm text-[#111B21] dark:text-[#E9EDEF]">
              {{ previewCategory }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
